import React, { useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { SettingsContext, UserContext, useEntitlement } from "../../../contexts";
import { Button, Icon, IconButton, Spinner, Tabs } from "../../../components/ui";
import StatePanel from "../../../components/ui/StatePanel";
import { Loading, Money } from "../../../components";
import { InlineSurfaceContext } from "../../../core/components/InlineOrModal/inlineSurface";
import { isRcOrDaoStaff } from "../../../core/config/storeId";
import { usePriceAnalytics } from "../../../product/hooks";
import { useQuote } from "../../hooks";
import AiPricing from "../AiPricing";
import AssignReviewer from "../QuoteActions/AssignReviewer";
import { SelectListingPanel } from "../QuoteActions/SelectListing/SelectListingModal";
import QuoteForm from "../Quote/QuoteForm";
import { CostsForm } from "../Quote/CostsForm";
import { MarketDataForm } from "../Quote/MarketDataForm";
import { PricingForm } from "../Quote/PricingForm";
import Notes from "../Quote/Notes";
import PriceIQ from "../Quote/PriceIQ";
import "../Quote/PriceIQ.scss";
import Updates from "../Quote/Updates";
import Profit from "../Quote/Profit";
import NetProfit from "../Quote/NetProfit";
import LeadsList from "../Quote/Leads/LeadsList";
import { getProfitPrices } from "../Quote/quoteViewHelpers";
import "../Quote/rc-quote.scss";
import useQuoteActions from "./useQuoteActions";
import QuoteStageTrack, { QuoteStageBadge } from "./QuoteStageTrack";
import WorkspacePricing from "./WorkspacePricing";
import "./quote-workspace.scss";


// The workspace shows the full quoting toolset — PRODUCT PRICING on order
// quotes and TRADE / MARKET MEDIAN on sell quotes — whatever the store flag says
// about the modal. Set to false to hide those two inputs again in the workspace
// (they keep saving silently, as before). The flag itself still decides
// workspace vs. modal.
const WORKSPACE_PRICING_FIELDS = true;

const TAB_KEY = "qws.tab";
const readTab = () => {
  try {
    return window.localStorage.getItem(TAB_KEY) || "details";
  } catch (e) {
    return "details";
  }
};
const saveTab = (id) => {
  try {
    window.localStorage.setItem(TAB_KEY, id);
  } catch (e) {
    // Storage blocked (private window) — the tab still switches.
  }
};

const Tile = ({ label, children, tone }) => (
  <div className={`qws-tile${tone ? ` qws-tile--${tone}` : ""}`}>
    <span className="qws-tile__label">{label}</span>
    <span className="qws-tile__value">{children}</span>
  </div>
);

const money = (amount, currency) =>
  amount ? <Money amount={amount} currency={(currency || "gbp").toLowerCase()} className="money" /> : "—";

/**
 * The quote, on the page — the no-pop-up quoting flow for stores on the new
 * setup — every store in the RC app (see quoteWorkspaceEnabled). Everything the quote modal offers is
 * here, inline:
 *
 *   head   quote number · stage · the stage track
 *   bar    the tabs (fixed — they never move when another tab is picked) ·
 *          "More" · ONE next step
 *   tabs   Details · Costs · CH24 market data (sell) · Pricing · AI ·
 *          Leads (sell) · Activity
 *
 * Every field saves on change. The DAO setup keeps the modal (QuoteView).
 */
export default function QuoteWorkspace({ id, item, onQuoteSaved, onBack, navigateOnSave = false }) {
  const quoteId = id === "new" || id === "select-listing" ? id : parseInt(id, 10);
  const { quote, isLoading, isError } = useQuote(quoteId);
  const queryClient = useQueryClient();
  const { isMerchant } = useContext(SettingsContext);
  const { user, activeStoreId } = useContext(UserContext);
  const hasDataValidation = useEntitlement("data_validation_screen");
  const hasPriceIQ = useEntitlement("priceiq_quotes");
  const showPriceIQ = isRcOrDaoStaff(user, activeStoreId);
  const { analytics } = usePriceAnalytics(item?.product);

  const [profitPrices, setProfitPrices] = useState({});
  const [consignPrices, setConsignPrices] = useState({});
  const [moreOpen, setMoreOpen] = useState(false);
  const [tab, setTab] = useState(readTab);
  // The ⋯ menu item running right now (its spinner; the others wait).
  const [running, setRunning] = useState(null);
  const quoteSubmitRef = useRef(null);
  const panelRef = useRef(null);
  const menuRef = useRef(null);
  const headRef = useRef(null);
  const actions = useQuoteActions(quote, onQuoteSaved);
  const overlayOpen = moreOpen || !!actions.panel;

  const closeMenu = () => {
    setMoreOpen(false);
    actions.setPanel(null);
  };

  // A step that needs a choice (reviewer, send as, listing) opens inside the
  // menu; anything else runs from the menu and closes it when it is done.
  const runSecondary = (a) => {
    if (a.panel) {
      a.onClick();
      return;
    }
    setRunning(a.id);
    Promise.resolve()
      .then(a.onClick)
      // Failures are already on screen (the quote hooks' toasts).
      .catch(() => {})
      .finally(() => {
        setRunning(null);
        closeMenu();
      });
  };

  // Opening a quote brings it into view: in the request flow it opens under
  // the quote list, often below the bottom of the screen. Once per quote.
  const shownId = useRef(null);
  useEffect(() => {
    if (isLoading || shownId.current === id) return;
    shownId.current = id;
    // Only when it opened low down — a quote already in view stays put, so
    // stepping through the list does not jump the page each time.
    const top = headRef.current?.getBoundingClientRect?.().top ?? 0;
    if (top > window.innerHeight * 0.6) {
      // Next frame: a scroll started in the same frame as the click gets
      // cancelled by the layout the open quote causes.
      const head = headRef.current;
      // Only the panel the quote sits in scrolls — scrollIntoView also moved
      // the page and pushed the app header out of view.
      requestAnimationFrame(() => {
        if (!head) return;
        let box = head.parentElement;
        while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) box = box.parentElement;
        const scroller = box || document.scrollingElement;
        // 16px of breathing room above the quote heading.
        const offset = head.getBoundingClientRect().top - (box ? box.getBoundingClientRect().top : 0) - 16;
        scroller?.scrollBy?.({ top: offset, behavior: "smooth" });
      });
    }
  }, [id, isLoading]);

  useEffect(() => {
    if (quote) setProfitPrices((prices) => getProfitPrices(quote, prices, false));
  }, [quote]);

  // A new stage brings a new set of actions — close whatever was open.
  useEffect(() => {
    closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.status, quote?.listing_id]);

  // Outside click / Escape closes the menu. Pickers inside it (the reviewer
  // Select) draw their option lists in a layer on <body> — those count as inside.
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (e.target.closest?.(".ft-pop, .select__menu, .ft-modal, .ReactModal__Overlay")) return;
      closeMenu();
    };
    // Capture phase + stop: Escape closes the menu only, not the Orders drawer.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeMenu();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayOpen]);

  if (isError && !quote) {
    return (
      <div className="qws rc-quote">
        <StatePanel
          state="error"
          compact
          title="Couldn't load this quote"
          description="Something went wrong on our side — try again."
          onRetry={() => queryClient.refetchQueries({ queryKey: ["quote", quoteId] })}
        />
      </div>
    );
  }

  if (!quote && isLoading) {
    return (
      <div className="qws rc-quote">
        <Loading />
      </div>
    );
  }

  const isSell = item?.request_type === "sell";
  const isNew = !quote?.id;
  const type = isSell ? "sell" : "order";
  const marketPoints = Number(analytics?.market?.points_12m ?? analytics?.market?.year?.points ?? 0);

  const tabs = [
    { id: "details", label: "Details" },
    !isNew && { id: "costs", label: "Costs" },
    !isNew && isSell && { id: "ch24", label: "CH24 market data" },
    item?.product?.id && { id: "pricing", label: "Pricing" },
    !isMerchant && hasDataValidation && item?.product && { id: "ai", label: "AI" },
    !isMerchant && isSell && { id: "leads", label: "Leads" },
    !isMerchant && !isNew && { id: "activity", label: "Activity" },
  ].filter(Boolean);
  const active = tabs.some((t) => t.id === tab) ? tab : "details";

  const pick = (next) => {
    // Never let the content area shrink on a tab switch: a shorter tab would
    // pull the scroll position back and the tab row would jump down the screen.
    const panel = panelRef.current;
    if (panel) panel.style.minHeight = `${panel.offsetHeight}px`;
    setTab(next);
    saveTab(next);
  };

  // Profit / Net need both sides; until then they read "—" like every other
  // empty figure (Profit and NetProfit themselves draw a 💰 placeholder).
  const p = profitPrices;
  const hasProfit = isSell ? !!(p.buy && (p.trade || p.market)) : !!(p.buy && p.sell);
  // The figures strip under the tabs, on every tab. It reads the form as it is
  // typed into (QuoteForm feeds setProfitPrices on change), so the figures move
  // with the fields below them.
  const kpis = (
    <div className="qws-tiles qws-kpis" aria-live="polite">
      <Tile label="Buying">{money(p.buy, p.buyCurrency)}</Tile>
      {isSell ? (
        <Tile label="Trade median">{money(p.trade, "gbp")}</Tile>
      ) : (
        <Tile label="Selling">{money(p.sell, p.sellCurrency)}</Tile>
      )}
      <Tile label={isSell ? "Profit range" : "Profit"} tone={hasProfit ? "success" : undefined}>
        {hasProfit ? <Profit {...p} type={type} /> : "—"}
      </Tile>
      <Tile label="Net profit" tone={hasProfit ? "success" : undefined}>
        {hasProfit ? <NetProfit {...p} type={type} listing={quote?.listing} quote={quote} /> : "—"}
      </Tile>
    </div>
  );

  const details = (
    <>
      <div className="qws-card">
        <QuoteForm
          {...{
            id,
            quoteSubmitRef,
            quote,
            item,
            onDone: onQuoteSaved,
            navigateOnSave,
            setProfitPrices,
            consignPrices,
          }}
          autoSave
          layout="grid"
          productPricing={WORKSPACE_PRICING_FIELDS}
        />
        {!isNew && (
          <div className="qws-notes">
            <span className="qws-notes__label">Notes</span>
            <Notes quote={quote} />
          </div>
        )}
      </div>

      {!isNew && isSell && (
        <div className="qws-card">
          <h4 className="qws-card__title">Consignment pricing</h4>
          <PricingForm {...{ id, quoteSubmitRef, quote, item, setConsignPrices }} autoSave layout="grid" />
        </div>
      )}

      {/* The PriceIQ cards (images) start closed — open, they are taller
          than the form and pushed the flow's Save / Next off the screen.
          They load while closed, so opening one is instant. This year first. */}
      {!isNew && showPriceIQ && hasPriceIQ && item?.product && (
        <div className="qws-priceiq">
          {item?.year && (
            <details className="qws-fold">
              <summary className="qws-fold__head">
                <Icon name="ChevronRight" size="sm" className="qws-fold__chevron" />
                PriceIQ · {item.year}
                <span className="qws-fold__hint">Show card</span>
              </summary>
              <PriceIQ item={item} isVisible />
            </details>
          )}
          <details className="qws-fold">
            <summary className="qws-fold__head">
              <Icon name="ChevronRight" size="sm" className="qws-fold__chevron" />
              PriceIQ · all years
              <span className="qws-fold__hint">Show card</span>
            </summary>
            <PriceIQ item={item} isVisible excludeYear />
          </details>
        </div>
      )}
    </>
  );

  const panels = {
    details,
    costs: !isNew && (
      <div className="qws-card">
        <h4 className="qws-card__title">Costs</h4>
        <CostsForm id={id} quote={quote} item={item} autoSave layout="grid" />
      </div>
    ),
    ch24: !isNew && isSell && <MarketDataForm id={id} quote={quote} item={item} autoSave layout="grid" />,
    pricing: item?.product?.id && (
      <>
        {marketPoints === 0 && (
          <p className="qws-hint">
            <Icon name="Info" size="sm" /> No market prices in the last 12 months — approve some in AI.
          </p>
        )}
        <WorkspacePricing product={item.product} />
      </>
    ),
    ai: item?.product && (
      <AiPricing product={item.product} requestItem={item} quoteType={item.request_type} variant="workspace" />
    ),
    leads: isSell && (
      <div className="qws-card">
        <LeadsList product_id={item.product_id} />
      </div>
    ),
    activity: !isNew && (
      <div className="qws-card">
        <Updates quote={quote} />
      </div>
    ),
  };

  return (
    // No pop-ups anywhere in here: shared sub-tools that open a modal elsewhere
    // (remove price, New Supplier, AI extraction, leads) open in place instead.
    <InlineSurfaceContext.Provider value>
    <div className="qws rc-quote" data-tab={active}>
      <header className="qws-head" ref={headRef}>
        <h3 className="qws-title">{isNew ? "New quote" : `Quote #${quote.id}`}</h3>
        {!isNew && <QuoteStageBadge quote={quote} />}
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            className="qws-close"
            aria-label="Close quote"
            leadingIcon={<Icon name="X" size="sm" />}
            onClick={onBack}
          >
            Close
          </Button>
        )}
      </header>

      {!isNew && <QuoteStageTrack quote={quote} />}

      {/* Pinned while the tab's content scrolls under it: the next step and
          its ⋯ menu, then the tabs. Nothing in here ever moves. */}
      <div className="qws-bar">
        {!isNew && (actions.primary || actions.secondary.length > 0 || actions.note) && (
          <div className="qws-bar__actions" ref={menuRef}>
            {actions.note && <span className="qws-bar__note">{actions.note}</span>}
            {actions.primary && (
              <Button
                variant="primary"
                size="lg"
                className="qws-bar__primary"
                loading={actions.primary.loading}
                aria-expanded={actions.primary.panel ? actions.panel === actions.primary.panel : undefined}
                leadingIcon={<Icon name={actions.primary.icon} size="sm" />}
                onClick={() => {
                  setMoreOpen(false);
                  actions.primary.onClick();
                }}
              >
                {actions.primary.label}
              </Button>
            )}
            {actions.secondary.length > 0 && (
              <IconButton
                size="lg"
                round
                className="qws-bar__more"
                label="More actions"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                onClick={() => {
                  actions.setPanel(null);
                  setMoreOpen((o) => !o);
                }}
              >
                <Icon name="MoreHorizontal" size="sm" />
              </IconButton>
            )}

            {/* The ⋯ menu and every step that needs a choice first (reviewer,
                send as, listing) open here, OVER the fields — nothing below
                moves. */}
            {overlayOpen && (
              <div className={`qws-menu${actions.panel === "listing" ? " qws-menu--wide" : ""}`}>
                {actions.panel && moreOpen && (
                  <button type="button" className="qws-menu__back" onClick={() => actions.setPanel(null)}>
                    <Icon name="ChevronLeft" size="sm" /> More actions
                  </button>
                )}

                {!actions.panel && (
                  <ul className="qws-menu__list" role="menu" aria-label="More actions">
                    {actions.secondary.map((a) => (
                      <li key={a.id} role="none">
                        <button
                          type="button"
                          role="menuitem"
                          className="qws-menu__item"
                          disabled={a.disabled || !!running}
                          aria-busy={running === a.id || undefined}
                          onClick={() => runSecondary(a)}
                        >
                          {running === a.id || a.loading ? (
                            <Spinner size="sm" />
                          ) : (
                            <Icon name={a.icon} size="sm" />
                          )}
                          <span>{a.label}</span>
                          {a.panel && <Icon name="ChevronRight" size="sm" className="qws-menu__chevron" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {actions.panel === "assign" && (
                  <div className="qws-menu__panel">
                    <AssignReviewer quote={quote} initiallyOpen onDone={closeMenu} />
                  </div>
                )}

                {actions.panel === "send" && (
                  <div className="qws-menu__panel qws-menu__send" role="group" aria-label="Send quote as">
                    <span className="qws-menu__label">Send quote as</span>
                    {[
                      ["SP", "Selling price"],
                      ["CONSIGN", "Consignment"],
                      ["SP_CONSIGN", "Both"],
                    ].map(([value, label]) => (
                      <Button
                        key={value}
                        variant={value === "SP_CONSIGN" ? "primary" : "secondary"}
                        size="lg"
                        fullWidth
                        disabled={actions.sending}
                        // A failure is already a toast — close either way.
                        onClick={() => actions.send(value).then(closeMenu, closeMenu)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                )}

                {actions.panel === "listing" && (
                  <div className="qws-menu__panel qws-menu__listing">
                    <SelectListingPanel
                      productId={quote.product_id}
                      currentListingId={quote.listing_id}
                      isSubmitting={actions.isLinking}
                      onClose={() => (moreOpen ? actions.setPanel(null) : closeMenu())}
                      onConfirm={(listing) => actions.chooseListing(listing).then(closeMenu, closeMenu)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Tabs
          variant="underline"
          className="qws-tabs"
          aria-label="Quote sections"
          items={tabs.map((t) => ({ value: t.id, label: t.label }))}
          value={active}
          onChange={pick}
        />
      </div>

      {kpis}

      <div
        ref={panelRef}
        className="qws-panel rc-quote__section"
        role="tabpanel"
        aria-label={tabs.find((t) => t.id === active)?.label}
      >
        {panels[active]}
      </div>
    </div>
    </InlineSurfaceContext.Provider>
  );
}
